import { useEffect, useRef } from 'react';
import { useI18n } from '../i18n';
import { storyContent } from '../content/storyContent';
import { measureStory, storyState, range, clamp01 } from '../scene/storyTimeline';
import './ScrollNarrative.css';

// Keep the entire line in the document from the start. Only the ink is revealed,
// so typing cannot change line breaks, height or the position of the paragraphs.
function TypedLine({text,as:Tag='h2',className='',speed=12,delay=150,testId}){
  return <Tag className={`story-typed ${className}`} aria-label={text} data-type-speed={speed} data-type-delay={delay} data-testid={testId}>
    <span aria-hidden="true" className="story-lettering">{Array.from(text).map((c,i)=><span className="story-char" key={i} style={{opacity:0}}>{c}</span>)}</span>
    <span className="story-caret" aria-hidden="true"><i/><i/><i/><i/><i/><i/><i/><i/></span>
  </Tag>;
}
export function ScrollNarrative({enabled,reduced=false,replayToken=0}){
  const {language}=useI18n(),copy=storyContent[language]||storyContent.ru,root=useRef(null),stateRef=useRef({enabled,reduced}),wakeRef=useRef(null);
  stateRef.current={enabled,reduced};
  useEffect(()=>{wakeRef.current?.();},[enabled,reduced]);
  useEffect(()=>{
    const stage=root.current,panes=Array.from(stage.querySelectorAll('[data-story-beat]'));
    let layout=measureStory(),raf=0,disposed=false,current=-1,wasShown=false,lastScroll=NaN,cachedState=null;
    function wake(){if(!disposed&&!document.hidden&&!raf)raf=requestAnimationFrame(frame);}
    function stop(){if(raf)cancelAnimationFrame(raf);raf=0;}
    const visibility=()=>{if(document.hidden)stop();else wake();};
    wakeRef.current=wake;
    const records=panes.map(pane=>({pane,entered:0,seen:false,types:Array.from(pane.querySelectorAll('[data-type-speed]')).map(el=>{
      const chars=Array.from(el.querySelectorAll('.story-char')),speed=Number(el.dataset.typeSpeed);
      return {el,chars,caret:el.querySelector('.story-caret'),last:-1,speed,delay:Number(el.dataset.typeDelay),duration:chars.reduce((sum,c)=>sum+speed+(c.textContent===' '?speed*.48:0),0),positions:[],maxLeft:0};
    }),reveals:Array.from(pane.querySelectorAll('[data-reveal-delay]'))}));
    const placeCaret=(type,count)=>{
      const position=type.positions[Math.max(0,count-1)];if(!position)return;
      type.caret.style.left=`${Math.min(type.maxLeft,position.left)}px`;
      type.caret.style.top=`${position.top}px`;
    };
    // Batch layout reads before writes. Font/viewport changes also update hidden
    // panes, retaining the overflow fix without measuring every typed character.
    const recalc=()=>{
      if(disposed)return;
      layout=measureStory();cachedState=null;
      records.forEach(rec=>rec.types.forEach(type=>{
        type.maxLeft=Math.max(0,type.el.clientWidth-type.caret.offsetWidth);
        type.positions=type.chars.map(char=>({left:char.offsetLeft+char.offsetWidth+4,top:char.offsetTop+Math.max(0,(char.offsetHeight-30)/2)}));
      }));
      records.forEach(rec=>rec.types.forEach(type=>placeCaret(type,Number(type.el.dataset.typed)||0)));
      wake();
    };
    const ro=new ResizeObserver(recalc);ro.observe(document.getElementById('scroll-story')||document.querySelector('[data-testid="scroll-story"]'));window.addEventListener('resize',recalc);
    window.addEventListener('scroll',wake,{passive:true});document.addEventListener('visibilitychange',visibility);
    document.fonts?.addEventListener('loadingdone',recalc);document.fonts?.ready.then(recalc);recalc();
    function frame(now){
      raf=0;if(disposed||document.hidden)return;
      const {enabled:show,reduced:reduce}=stateRef.current;
      if(!show){stage.style.visibility='hidden';wasShown=false;return;}
      if(!wasShown){recalc();wasShown=true;}
      if(!cachedState||lastScroll!==scrollY){cachedState=storyState(scrollY,layout);lastScroll=scrollY;stage.dataset.beat=cachedState.beat;stage.dataset.segment=cachedState.segment.toFixed(3);stage.dataset.handoff=cachedState.handoff.toFixed(3);stage.style.visibility=cachedState.visible?'visible':'hidden';stage.style.transform=cachedState.handoff>0?`translateY(${cachedState.y}px) scale(${cachedState.scale})`:'';}
      const state=cachedState;
      if(!state.visible){if(current>=0){records[current].pane.setAttribute('aria-hidden','true');records[current].pane.setAttribute('inert','');records[current].seen=true;current=-1;}stop();return;}
      const index=state.beat,rec=records[index],local=state.segment-index;
      if(index!==current){
        if(current>=0){const prev=records[current];prev.pane.style.opacity='0';prev.pane.style.pointerEvents='none';prev.pane.setAttribute('aria-hidden','true');prev.pane.setAttribute('inert','');prev.seen=true;}
        current=index;rec.entered=rec.seen?now-20000:now;rec.pane.removeAttribute('inert');rec.pane.setAttribute('aria-hidden','false');
      }
      const elapsed=now-rec.entered;
      const entry=index===0?range(0,450,elapsed):range(-.5,-.22,local);
      const exit=index===6?1:1-range(.25,.5,local);
      const handoffFade=1-range(.18,.70,state.handoff);
      const opacity=reduce?(state.handoff<.7?1:0):entry*exit*handoffFade;
      rec.pane.style.opacity=String(opacity);rec.pane.style.pointerEvents=opacity>.6?'auto':'none';
      rec.pane.style.transform=reduce?'none':`translateY(${(1-entry)*14-(1-exit)*12}px)`;
      rec.pane.dataset.opacity=opacity.toFixed(3);
      // Rapid scrolling advances the ink too: a newly arrived scene never traps
      // the reader waiting for a long typewriter to finish before they can leave.
      const readingProgress=index===6?clamp01((scrollY-layout.starts[6])/Math.max(1,layout.handoffStart-layout.starts[6])):local;
      const scrub=range(.04,.27,readingProgress);
      let allTyped=true;
      rec.types.forEach(type=>{
        const timed=clamp01((elapsed-type.delay)/Math.max(1,type.duration));
        const fraction=reduce||rec.seen?1:Math.max(timed,scrub);
        const count=Math.min(type.chars.length,Math.floor(type.chars.length*fraction));
        if(count!==type.last){
          const from=type.last<0?0:Math.min(type.last,count),to=type.last<0?type.chars.length:Math.max(type.last,count);
          for(let i=from;i<to;i++)type.chars[i].style.opacity=i<count?'1':'0';type.last=count;
          placeCaret(type,count);
        }
        type.el.dataset.typed=count;type.el.dataset.total=type.chars.length;
        type.caret.style.opacity=reduce||count===0||count===type.chars.length?'0':'1';
        if(count<type.chars.length)allTyped=false;
      });
      rec.reveals.forEach(el=>{const delay=Number(el.dataset.revealDelay),timed=range(delay,delay+650,elapsed),value=reduce||rec.seen?1:Math.max(timed,scrub);el.style.opacity=String(value);el.style.transform=reduce?'none':`translateY(${(1-value)*12}px)`;});
      const complete=allTyped&&rec.reveals.every(el=>Number(el.style.opacity)>.99);
      rec.pane.dataset.complete=String(complete);
      // The scrim shares the exact same scale/translation as the world, so no
      // text or contrast plate can remain floating over the light sections.
      stage.style.setProperty('--scrim-strength',String(1-range(.35,.92,state.handoff)));
      if(allTyped&&rec.reveals.every(el=>Number(el.style.opacity)===1)&&(index!==0||entry===1))stop();else wake();
    }
    wake();
    return()=>{disposed=true;stop();wakeRef.current=null;ro.disconnect();window.removeEventListener('resize',recalc);window.removeEventListener('scroll',wake);document.removeEventListener('visibilitychange',visibility);document.fonts?.removeEventListener('loadingdone',recalc);};
  },[language,replayToken]);
  return <div className="scroll-narrative" ref={root} data-testid="scroll-narrative" data-language={language}>
    <div className="story-scrim" aria-hidden="true"/>
    <div className="story-copy-column">
      <article className="story-pane story-intro" data-story-beat="0" data-testid="story-beat-0" aria-hidden="true" inert>
        <div className="story-eyebrow" aria-hidden="true"><span>01</span><i/><span>AIDAGIS</span></div>
        <TypedLine text={copy.title} as="h1" className="story-headline" speed={50} delay={250} testId="story-title"/>
        <p className="story-subtitle" data-reveal-delay="1500" data-testid="story-subtitle">{copy.subtitle}</p>
      </article>
      {copy.results.map((item,i)=><article className="story-pane story-result" data-story-beat={i+1} data-testid={`story-beat-${i+1}`} key={`result-${i}`} aria-hidden="true" inert>
        <div className="story-eyebrow"><span className="story-small-index" aria-hidden="true">0{i+1} / 03</span><i/><span>{copy.experience}</span></div>
        <TypedLine text={item.text} className="story-result-claim" speed={16} testId={`story-result-claim-${i+1}`}/>
        <p className="story-outcome" data-reveal-delay="900" data-testid={`story-outcome-${i+1}`}>{item.result}</p>
      </article>)}
      <article className="story-pane story-gap" data-story-beat="4" data-testid="story-beat-4" aria-hidden="true" inert>
        <div className="story-eyebrow" aria-hidden="true"><span>02</span><i/><span>AIDAGIS</span></div>
        {copy.gap.map((p,i)=><p key={i} className={i===0?'story-gap-lead':'story-gap-body'} data-reveal-delay={i*500+120} data-testid={`story-gap-${i+1}`}>{p}</p>)}
      </article>
      {[0,1].map(part=><article className="story-pane story-beliefs" data-story-beat={part+5} data-testid={`story-beat-${part+5}`} key={`beliefs-${part}`} aria-hidden="true" inert>
        <div className="story-eyebrow"><span aria-hidden="true">03</span><i/><span>{copy.beliefsTitle}</span></div>
        <div className="story-belief-list">{copy.beliefs.slice(part*2,part*2+2).map((belief,j)=><div className="story-belief" key={j}>
          <span className="belief-number" aria-hidden="true">0{part*2+j+1}</span>
          <div><TypedLine text={belief.claim} className="story-belief-claim" speed={12} delay={j*1050+160} testId={`story-belief-claim-${part*2+j+1}`}/>{belief.text&&<p className="story-belief-body" data-reveal-delay={j*1050+850} data-testid={`story-belief-body-${part*2+j+1}`}>{belief.text}</p>}</div>
        </div>)}</div>
      </article>)}
    </div>
  </div>;
}
